/**
 * Verbrauch (Kapitel K, PV-K04 bis PV-K06).
 *
 * Eingegeben wird der Jahresverbrauch aus der Abrechnung des Kunden. Daraus
 * wird ueber ein hinterlegtes Standardlastprofil ein Tages- und Monatsverlauf
 * gebildet — das reicht fuer eine Angebotsrechnung.
 *
 * WICHTIG ZUM PROFIL: Was hier liegt, ist ein nachgebildeter H0-Verlauf, keine
 * amtliche Tabelle. Die Form stimmt (Morgen- und Abendspitze, Winter hoeher als
 * Sommer, Wochenende flacher), die Zahlen sind gerundet. Sobald das echte
 * Profil von E-Control oder dem Netzbetreiber vorliegt, wird es hier ersetzt —
 * die Struktur bleibt dieselbe. Bis dahin ist `istPlatzhalter` gesetzt, und die
 * Oberflaeche sagt es dazu.
 */
/** Aus einem Stundenverlauf ein normiertes Profil machen. */
function normiere(werte) {
    const summe = werte.reduce((a, b) => a + b, 0);
    return werte.map((wert) => wert / summe);
}
// Grundform eines Haushaltstages: nachts wenig, Morgenspitze, Mittagsdelle,
// deutliche Abendspitze.
const WERKTAG = normiere([
    22, 18, 16, 16, 17, 24, 42, 55, 50, 44, 42, 45,
    48, 44, 40, 42, 52, 70, 88, 92, 82, 66, 46, 30,
]);
const SAMSTAG = normiere([
    24, 20, 18, 17, 18, 22, 30, 42, 52, 56, 56, 56,
    56, 52, 48, 48, 56, 72, 86, 88, 80, 68, 50, 34,
]);
const SONNTAG = normiere([
    26, 22, 19, 18, 18, 20, 26, 36, 48, 56, 60, 62,
    60, 54, 48, 46, 54, 70, 84, 86, 76, 62, 46, 32,
]);
/**
 * Standardlastprofil H0 (nachgebildet).
 *
 * Im Winter liegt die Abendspitze frueher und hoeher, im Sommer flacher — das
 * bildet der Faktor je Jahreszeit ab, ohne drei getrennte Tabellen zu pflegen.
 */
export const PROFIL_H0 = {
    name: 'Haushalt H0 (nachgebildet)',
    istPlatzhalter: true,
    stunden: {
        winter: {
            werktag: normiere(WERKTAG.map((wert, stunde) => wert * (stunde >= 16 && stunde <= 21 ? 1.18 : 0.95))),
            samstag: normiere(SAMSTAG.map((wert, stunde) => wert * (stunde >= 16 && stunde <= 21 ? 1.15 : 0.96))),
            sonntag: normiere(SONNTAG.map((wert, stunde) => wert * (stunde >= 16 && stunde <= 21 ? 1.15 : 0.96))),
        },
        uebergang: { werktag: WERKTAG, samstag: SAMSTAG, sonntag: SONNTAG },
        sommer: {
            werktag: normiere(WERKTAG.map((wert, stunde) => wert * (stunde >= 10 && stunde <= 16 ? 1.08 : 0.96))),
            samstag: normiere(SAMSTAG.map((wert, stunde) => wert * (stunde >= 10 && stunde <= 16 ? 1.08 : 0.96))),
            sonntag: normiere(SONNTAG.map((wert, stunde) => wert * (stunde >= 10 && stunde <= 16 ? 1.08 : 0.96))),
        },
    },
    // Winter deutlich ueber Sommer — Licht und Heizungshilfsstrom.
    monate: normiere([102, 92, 90, 80, 76, 72, 72, 74, 78, 88, 96, 100]),
};
export function verbrauchDesZusatzverbrauchers(v) {
    if (v.art === 'elektroauto') {
        return ((v.jahreskilometer ?? 0) * (v.verbrauchJe100km ?? 18)) / 100;
    }
    return v.jahresverbrauchKwh ?? 0;
}
const TAGE_JE_MONAT = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function jahreszeitVonMonat(monat) {
    if (monat <= 2 || monat >= 10)
        return 'winter'; // Nov bis Maerz (0 = Jaenner)
    if (monat >= 4 && monat <= 8)
        return 'sommer'; // Mai bis September
    return 'uebergang';
}
/**
 * Jahresverbrauch auf Stunden verteilen.
 *
 * Der 1. Jaenner wird als Montag angenommen — fuer eine Angebotsrechnung ist
 * das ohne Belang, und es haelt das Ergebnis reproduzierbar.
 */
export function verbrauchsverlauf(eingabe) {
    const profil = eingabe.profil ?? PROFIL_H0;
    const zusatz = eingabe.zusatzverbraucher ?? [];
    const jahrZusatzKwh = zusatz.reduce((summe, v) => summe + verbrauchDesZusatzverbrauchers(v), 0);
    const stunden = [];
    const monate = [];
    let tagImJahr = 0;
    for (let monat = 0; monat < 12; monat++) {
        const tage = TAGE_JE_MONAT[monat];
        const jahreszeit = jahreszeitVonMonat(monat);
        const monatsverbrauch = eingabe.monatswerteKwh?.[monat] ??
            eingabe.jahresverbrauchKwh * (profil.monate[monat] ?? 1 / 12);
        // Innerhalb des Monats gleichmaessig auf die Tage, dann nach Tagesart.
        const gewichtJeTag = [];
        for (let tag = 0; tag < tage; tag++) {
            const wochentag = (tagImJahr + tag) % 7;
            gewichtJeTag.push(wochentag === 5 ? 1.02 : wochentag === 6 ? 0.98 : 1.0);
        }
        const gewichtsumme = gewichtJeTag.reduce((a, b) => a + b, 0);
        let monatssumme = 0;
        for (let tag = 0; tag < tage; tag++) {
            const wochentag = (tagImJahr + tag) % 7;
            const tagesart = wochentag === 5 ? 'samstag' : wochentag === 6 ? 'sonntag' : 'werktag';
            const tagesverbrauch = monatsverbrauch * (gewichtJeTag[tag] / gewichtsumme);
            const verlauf = profil.stunden[jahreszeit][tagesart];
            for (let stunde = 0; stunde < 24; stunde++) {
                const wert = tagesverbrauch * (verlauf[stunde] ?? 1 / 24);
                stunden.push(wert);
                monatssumme += wert;
            }
        }
        tagImJahr += tage;
        monate.push(monatssumme);
    }
    // Zusatzverbraucher oben drauf, jeder mit seinem eigenen Muster.
    for (const verbraucher of zusatz) {
        verteileZusatzverbraucher(stunden, monate, verbraucher);
    }
    return {
        stunden,
        monate,
        jahrGesamtKwh: stunden.reduce((a, b) => a + b, 0),
        jahrHaushaltKwh: eingabe.monatswerteKwh
            ? eingabe.monatswerteKwh.reduce((a, b) => a + b, 0)
            : eingabe.jahresverbrauchKwh,
        jahrZusatzKwh,
        profilIstPlatzhalter: profil.istPlatzhalter,
    };
}
/**
 * Zusatzverbraucher haben ein anderes Muster als der Haushalt:
 * die Waermepumpe laeuft im Winter, das Auto laedt abends oder tagsueber,
 * der Heizstab nimmt, was sonst ins Netz ginge.
 */
function verteileZusatzverbraucher(stunden, monate, verbraucher) {
    const gesamt = verbrauchDesZusatzverbrauchers(verbraucher);
    if (gesamt <= 0)
        return;
    // Gewicht je Monat und je Stunde, danach normiert.
    const monatsgewicht = verbraucher.art === 'waermepumpe' || verbraucher.art === 'heizstab'
        ? [180, 160, 120, 70, 30, 12, 10, 12, 30, 80, 140, 175]
        : new Array(12).fill(1);
    const stundengewicht = new Array(24).fill(0).map((_, stunde) => {
        switch (verbraucher.art) {
            case 'waermepumpe':
                // Laeuft rund um die Uhr, morgens und abends etwas mehr.
                return stunde >= 5 && stunde <= 8 ? 1.6 : stunde >= 16 && stunde <= 21 ? 1.4 : 1;
            case 'heizstab':
                // Nimmt den Ueberschuss — also mittags.
                return stunde >= 10 && stunde <= 15 ? 3 : stunde >= 8 && stunde <= 17 ? 1 : 0.1;
            case 'elektroauto':
                return verbraucher.ladenTagsueber
                    ? stunde >= 9 && stunde <= 15
                        ? 3
                        : 0.1
                    : stunde >= 18 || stunde <= 5
                        ? 2
                        : 0.1;
        }
    });
    const monatsSumme = monatsgewicht.reduce((a, b) => a + b, 0);
    const stundenSumme = stundengewicht.reduce((a, b) => a + b, 0);
    let index = 0;
    for (let monat = 0; monat < 12; monat++) {
        const tage = TAGE_JE_MONAT[monat];
        const monatsanteil = gesamt * ((monatsgewicht[monat] ?? 0) / monatsSumme);
        const jeTag = monatsanteil / tage;
        for (let tag = 0; tag < tage; tag++) {
            for (let stunde = 0; stunde < 24; stunde++) {
                const wert = jeTag * ((stundengewicht[stunde] ?? 0) / stundenSumme);
                stunden[index] = (stunden[index] ?? 0) + wert;
                index++;
            }
        }
        monate[monat] = (monate[monat] ?? 0) + monatsanteil;
    }
}
/**
 * Erzeugung auf Stunden verteilen — Gegenstueck zum Verbrauch, damit sich
 * beide in der Wirtschaftlichkeitsrechnung gegenueberstehen lassen.
 */
export function erzeugungsverlauf(monatsertragKwh, breiteGrad, sonnenstandJeStunde) {
    void breiteGrad;
    const stunden = [];
    let tagNummer = 1;
    for (let monat = 0; monat < 12; monat++) {
        const tage = TAGE_JE_MONAT[monat];
        const jeTag = (monatsertragKwh[monat] ?? 0) / tage;
        for (let tag = 0; tag < tage; tag++) {
            const form = [];
            let formsumme = 0;
            for (let stunde = 0; stunde < 24; stunde++) {
                const hoehe = Math.max(0, sonnenstandJeStunde(tagNummer, stunde + 0.5));
                form.push(hoehe);
                formsumme += hoehe;
            }
            for (let stunde = 0; stunde < 24; stunde++) {
                stunden.push(formsumme > 0 ? jeTag * ((form[stunde] ?? 0) / formsumme) : 0);
            }
            tagNummer++;
        }
    }
    return stunden;
}
//# sourceMappingURL=verbrauch.js.map